export interface ApiFieldError {
  context: string
  text: string
}

interface ApiValidationError {
  context: string
  error: {
    type: string
    code: string
    text: string
    parameters?: Record<string, string>
  }
}

interface ApiErrorBody {
  error?: string
  text?: string
  parameters?: Record<string, string>
  errors?: ApiValidationError[]
}

/** API message text with its `{{key}}` placeholders filled in from `parameters`. */
export function fillPlaceholders(text: string, parameters?: Record<string, string>): string {
  return Object.entries(parameters ?? {}).reduce<string>(
    (result, [key, val]) => result.split(`{{${key}}}`).join(val),
    text
  )
}

export class ApiError extends Error {
  readonly fieldErrors: ApiFieldError[]

  constructor(summary: string, fieldErrors: ApiFieldError[]) {
    super(summary)
    this.fieldErrors = fieldErrors
  }
}

export function apiErrorDescription(error: unknown): string {
  if (error instanceof ApiError) {
    const texts = error.fieldErrors.map((e) => e.text)
    return texts.length ? texts.join('\n') : error.message
  }
  return (error as Error).message
}

interface FormWithFieldMeta {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFieldMeta(field: string, updater: (prev: any) => any): void
}

export function applyApiFieldErrors(form: FormWithFieldMeta, error: unknown): void {
  if (!(error instanceof ApiError)) return
  for (const fe of error.fieldErrors) {
    if (!fe.context) continue
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form.setFieldMeta(fe.context, (prev: any) => ({
      ...prev,
      errorMap: { ...prev.errorMap, onSubmit: fe.text },
    }))
  }
}

export async function throwApiError(res: Response, fallback: string): Promise<never> {
  let body: ApiErrorBody = {}
  try {
    body = await res.json()
  } catch {
    // non-JSON body, use fallback
  }

  const summary = body.text ? fillPlaceholders(body.text, body.parameters) : fallback
  const fieldErrors: ApiFieldError[] = body.errors?.map((e) => ({
    context: e.context ?? '',
    text: fillPlaceholders(e.error.text, e.error.parameters),
  })) ?? []

  throw new ApiError(summary, fieldErrors)
}
